import * as z from "zod";
import { isUniqueConstraintViolation } from "../dbErrors";
import { userFacingError } from "../authorization";
import { isValidSubjectPattern } from "../oidc/subjectMatch";
import { creatorColumns } from "../rbac/roles";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

const createServiceAccountFederationSchema = z.object({
  id: clientGeneratableIdSchema,
  serviceAccountId: z.string(),
  providerId: z.string(),
  name: z.string().min(1).max(120).optional(),
  subject: z.string().min(1).max(2048),
});

// Binding a service account to a registered provider is the per-account "which
// workload may assume me" step (AWS role trust-policy condition). It is gated on
// service_account:manage and grants the account no new authority -- it only lets
// a provider-issued token whose `sub` matches assume the account -- so manage
// alone is the right gate. The provider itself (the trust anchor) is registered
// separately under tenant:oidc_providers_manage.
export const createServiceAccountFederation = mutator({
  schema: createServiceAccountFederationSchema,
  resolve: {
    serviceAccount: { kind: "serviceAccount", from: "serviceAccountId" },
    provider: { kind: "oidcProvider", from: "providerId" },
  },
  permissions: [
    {
      permission: "service_account:manage",
      scopeId: ({ args }) => args.serviceAccount.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { id, serviceAccount, provider, name, subject },
  }) => {
    if (!isValidSubjectPattern(subject)) {
      throw userFacingError(
        "Subject may use a single trailing * only after a ':' or '/' boundary (e.g. repo:org/repo:*)"
      );
    }

    const now = Date.now();
    try {
      await tx.mutate.serviceAccountFederations.insert({
        id,
        serviceAccountId: serviceAccount.id,
        providerId: provider.id,
        name: name ?? provider.name,
        subject,
        disabled: false,
        ...creatorColumns(ctx),
        createdAt: now,
        updatedAt: now,
        tenantId: ctx.tenantId,
      });
    } catch (err) {
      if (
        isUniqueConstraintViolation(
          err,
          "service_account_federations_provider_subject_unique"
        )
      ) {
        throw userFacingError(
          "This provider and subject are already federated to a service account"
        );
      }
      throw err;
    }
  },
});
